/**
 * Разбор дельты по сделкам: что в ней видит человек.
 *
 * Столбик свечи понятен сам: «в этой свече покупали на столько-то
 * больше». Накопленная линия — нет: сумма с начала сбора зависит от
 * того, когда сбор начался, и одна огромная свеча держит её наверху
 * месяц. Поэтому здесь три вещи:
 *
 *   - накопление с начала периода (суток по умолчанию) — число, которое
 *     можно прочитать: «с начала суток покупали на 120 млн больше»;
 *   - расхождения цены с потоком — ради них линию и смотрят;
 *   - ситуация словами — вывод, который иначе пришлось бы делать самому.
 *
 * Модуль чистый: ни графика, ни хранилища. Всё, что он говорит
 * человеку, проверяется тестами на выдуманных рядах.
 */
/** Сколько свечей смотреть назад: и для расхождений, и для ситуации. */
export const DELTA_WINDOW = 20;
const DAY_MS = 86_400_000;
/** Начало периода, к которому принадлежит время: сутки или неделя UTC с понедельника. */
export function periodOf(time, anchor) {
    if (anchor === 'all')
        return 0;
    const day = Math.floor(time / DAY_MS);
    // 1 января 1970 — четверг: сдвиг на три дня ставит начало недели на понедельник.
    return anchor === 'day' ? day : Math.floor((day + 3) / 7);
}
export function analyzeDelta(candles, delta, anchor, window = DELTA_WINDOW, 
/**
 * Последняя свеча закрыта. На графике она обычно ещё набирается, и её
 * расхождение не проверяется; сервер считает по закрытой свече, и
 * пропустить её значило бы опоздать с оповещением на свечу.
 */
lastClosed = false) {
    const anchored = [];
    // Сквозная сумма — для расхождений: она не рвётся на границе суток.
    const running = [];
    let period = Number.NaN;
    let sum = null;
    let total = null;
    candles.forEach((candle, i) => {
        const current = periodOf(candle.time, anchor);
        if (current !== period) {
            period = current;
            sum = null;
        }
        const value = delta[i] ?? null;
        if (value !== null) {
            sum = (sum ?? 0) + value;
            total = (total ?? 0) + value;
        }
        anchored.push(sum);
        running.push(total);
    });
    const divergences = findDivergences(candles, delta, running, window, lastClosed);
    const lastIndex = candles.length - 1;
    const recent = windowSum(delta, lastIndex, window);
    return {
        delta,
        anchored,
        divergences,
        last: {
            delta: lastIndex >= 0 ? (delta[lastIndex] ?? null) : null,
            anchored: lastIndex >= 0 ? (anchored[lastIndex] ?? null) : null,
            window: recent?.sum ?? null,
        },
        situation: situationOf(candles, delta, window),
    };
}
/** Сумма и сумма модулей перевеса за окно, заканчивающееся на `end`. */
function windowSum(delta, end, window) {
    let sum = 0;
    let gross = 0;
    let bars = 0;
    for (let i = Math.max(0, end - window + 1); i <= end; i += 1) {
        const value = delta[i];
        if (value === null || value === undefined)
            continue;
        sum += value;
        gross += Math.abs(value);
        bars += 1;
    }
    return bars === 0 ? null : { sum, gross, bars };
}
/**
 * Расхождения цены с потоком.
 *
 * Цена обновила максимум за окно, а сквозной перевес — нет: рост идёт
 * без новых покупок по рынку, покупатели выдыхаются. Зеркально внизу:
 * новый минимум цены без нового минимума перевеса — продавцы слабеют.
 *
 * Формирующаяся свеча не проверяется: её максимум ещё может смениться,
 * и отметка появлялась бы и исчезала на глазах. Между двумя отметками
 * одного вида — не меньше половины окна: у вершины, которую цена
 * обновляет несколько свечей подряд, одно расхождение, а не пять.
 */
function findDivergences(candles, delta, running, window, lastClosed) {
    const out = [];
    const lastAt = { 'buyers-fading': -Infinity, 'sellers-fading': -Infinity };
    const gap = Math.max(2, Math.floor(window / 2));
    for (let i = window; i < candles.length - (lastClosed ? 0 : 1); i += 1) {
        const flow = running[i];
        const own = delta[i];
        if (flow === null || flow === undefined || own === null || own === undefined)
            continue;
        let highestPrice = -Infinity;
        let lowestPrice = Infinity;
        let highestFlow = -Infinity;
        let lowestFlow = Infinity;
        let seen = 0;
        for (let j = i - window; j < i; j += 1) {
            const candle = candles[j];
            highestPrice = Math.max(highestPrice, candle.high);
            lowestPrice = Math.min(lowestPrice, candle.low);
            const past = running[j];
            if (past === null || past === undefined)
                continue;
            highestFlow = Math.max(highestFlow, past);
            lowestFlow = Math.min(lowestFlow, past);
            seen += 1;
        }
        // Окно наполовину без сделок — сравнивать не с чем.
        if (seen < window / 2)
            continue;
        const candle = candles[i];
        if (candle.high > highestPrice && flow < highestFlow && i - lastAt['buyers-fading'] >= gap) {
            out.push({ time: candle.time, kind: 'buyers-fading', price: candle.high });
            lastAt['buyers-fading'] = i;
        }
        if (candle.low < lowestPrice && flow > lowestFlow && i - lastAt['sellers-fading'] >= gap) {
            out.push({ time: candle.time, kind: 'sellers-fading', price: candle.low });
            lastAt['sellers-fading'] = i;
        }
    }
    return out;
}
/**
 * Что происходит сейчас — словами.
 *
 * Сравниваются две вещи за последние свечи окна: куда ушла цена и кто
 * был активнее по рынку. Движение цены меряется в средних размахах
 * свечи, а не в процентах: два процента на минутках — обвал, на дневках
 * — тишина. Перевес меряется долей от всего проторгованного перевеса:
 * так «сильный» значит одно и то же у биткоина и у мелкой монеты.
 */
export function situationOf(candles, delta, window = DELTA_WINDOW) {
    const end = candles.length - 1;
    if (end < 2)
        return null;
    const flow = windowSum(delta, end, window);
    if (!flow || flow.bars < Math.min(window, candles.length) / 2 || flow.gross === 0)
        return null;
    const start = Math.max(0, end - window + 1);
    let range = 0;
    for (let i = start; i <= end; i += 1)
        range += candles[i].high - candles[i].low;
    range /= end - start + 1;
    if (range <= 0)
        return null;
    const move = (candles[end].close - candles[start].open) / range;
    const share = flow.sum / flow.gross;
    const up = move > 2;
    const down = move < -2;
    const buying = share > 0.15;
    const selling = share < -0.15;
    /*
     * Поглощение — вывод сильнее подтверждения, и порог у него выше.
     *
     * Фьючерсный поток в среднем чуть перекошен в продажи, и на пороге в
     * пятнадцать процентов «продажи поглощаются» звучало бы в трети всех
     * случаев — то есть ничего бы не значило. Сорок процентов на
     * двухмесячной истории BTC оставляют его там, где перевес явный.
     */
    const absorbingSells = share < -0.4;
    const absorbingBuys = share > 0.4;
    if (up && buying) {
        return { title: 'Рост на покупках', text: 'Цена растёт, и по рынку активно покупают — рост подтверждён деньгами.', tone: 'up' };
    }
    if (down && selling) {
        return { title: 'Падение на продажах', text: 'Цена падает, и по рынку активно продают — падение подтверждено деньгами.', tone: 'down' };
    }
    if (up && selling) {
        return {
            title: 'Рост без покупателей',
            text: 'Цена растёт, хотя по рынку больше продают. Рост держат лимитные заявки или он скоро выдохнется.',
            tone: 'warn',
        };
    }
    if (down && buying) {
        return {
            title: 'Падение против покупок',
            text: 'Цена падает, хотя по рынку больше покупают. Покупки гасят крупные продавцы заявками — пока они стоят, отскока нет.',
            tone: 'warn',
        };
    }
    if (absorbingSells) {
        return {
            title: 'Продажи поглощаются',
            text: 'По рынку продают, а цена стоит на месте: продажи забирают крупные покупатели заявками. Часто так идёт накопление перед ростом.',
            tone: 'warn',
        };
    }
    if (absorbingBuys) {
        return {
            title: 'Покупки поглощаются',
            text: 'По рынку покупают, а цена стоит на месте: покупки гасят крупные продавцы заявками. Часто так идёт раздача перед падением.',
            tone: 'warn',
        };
    }
    if (selling || buying) {
        return {
            title: selling ? 'Продавцы понемногу сильнее' : 'Покупатели понемногу сильнее',
            text: selling
                ? 'Цена стоит, по рынку немного больше продают — перевес пока слабый, явного давления нет.'
                : 'Цена стоит, по рынку немного больше покупают — перевес пока слабый, явного спроса нет.',
            tone: 'muted',
        };
    }
    if (up || down) {
        return {
            title: up ? 'Тихий рост' : 'Тихое падение',
            text: 'Цена движется, но явного перевеса покупок или продаж нет — движение идёт на слабом потоке.',
            tone: 'muted',
        };
    }
    return { title: 'Равновесие', text: 'Ни покупатели, ни продавцы не берут верх — рынок ждёт.', tone: 'muted' };
}
//# sourceMappingURL=delta-analysis.js.map